import { FC, useMemo, useState } from 'react';

import cn from 'classnames';
import { TbDotsVertical } from 'react-icons/tb';
import { capitalize, compact, startCase } from 'lodash';
import { dayjs } from 'utils/providers/Timezone';
import { HiOutlineCheckCircle } from 'react-icons/hi2';
import { GoChevronDown } from 'react-icons/go';
import { Dropdown, NavAction } from 'ui/components/nav';
import { DeleteItemModal, Status } from 'ui/components/common';
import { Avatar } from 'ui/components/img';
import { useGetSubscription } from 'modules/account/settings/hooks';
import c, { SIZE } from 'utils/constants';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import useModal from 'ui/partials/Modal/useModal';
import { AvatarGroup } from 'ui/components/img/Avatar';
import { Task } from 'utils/types';

import { useTasksContext } from '../containers/Tasks';
import { useDeleteTask, useUpdateTaskStatus } from '../hooks';
import getPriorityStyle from '../utils/getPriorityStyle';

const { ACTIONS, PERMISSION_KEYS } = c;

const STATUS_OPTIONS = [
  { label: 'PENDING', value: 'PENDING' },
  { label: 'IN PROGRESS', value: 'IN-PROGRESS' },
  { label: 'COMPLETED', value: 'COMPLETED' },
];

type PROPS = {
  task: Task;
  isLoading?: boolean;
};

const TaskTile: FC<PROPS> = ({ task, isLoading = false }) => {
  const canEdit = useCanRolePermissions(ACTIONS.UPDATE, PERMISSION_KEYS.TASKS);
  const canDelete = useCanRolePermissions(
    ACTIONS.DELETE,
    PERMISSION_KEYS.TASKS,
  );
  const { selectedTaskId, setSelectedTaskId } = useTasksContext();
  const { showModal } = useModal();

  const { data: subscription } = useGetSubscription();
  const { mutate: deleteTask, isLoading: deleteLoading } = useDeleteTask();
  const { mutate: updateStatus, isLoading: updateStatusLoading } =
    useUpdateTaskStatus();

  const [isChecked, setIsChecked] = useState(
    task.status === 'COMPLETED' || task.status === 'ARCHIVED',
  );

  const options = useMemo(() => {
    const opts = [
      {
        text: 'View full details',
        key: 'view',
        onClick: () => setSelectedTaskId?.(task._id),
      },
      canDelete && {
        text: 'Delete',
        key: 'delete',
        onClick: () => {
          showModal({
            title: 'Delete Task',
            content: (
              <DeleteItemModal
                onClickDelete={() => deleteTask(task._id)}
                text="Task"
                isLoading={deleteLoading}
                validation
              />
            ),
          });
        },
      },
    ];

    if (subscription?.isExpired) {
      return [
        {
          text: 'View full details',
          key: 'view',
          onClick: () => setSelectedTaskId?.(task._id),
        },
      ];
    }

    if (canEdit && task.status === 'COMPLETED') {
      opts.push({
        text: 'Move to archived',
        key: 'archive',
        onClick: () => updateStatus({ id: task._id, status: 'archived' }),
      });
    }

    if (canEdit && task.status === 'ARCHIVED') {
      opts.push({
        text: 'Unarchive',
        key: 'unarchive',
        onClick: () => updateStatus({ id: task._id, status: 'completed' }),
      });
    }

    return compact(opts);
  }, [
    canDelete,
    subscription?.isExpired,
    canEdit,
    task.status,
    task._id,
    setSelectedTaskId,
    showModal,
    deleteLoading,
    deleteTask,
    updateStatus,
  ]);

  const handleClickCheckbox = () => {
    updateStatus(
      { id: task._id, status: isChecked ? 'pending' : 'completed' },
      {
        onSuccess: () => setIsChecked((prev) => !prev),
      },
    );
  };

  const handleChangeStatus = (val: string) => {
    updateStatus(
      { id: task._id, status: val.toLowerCase() },
      {
        onSuccess: () => {
          setIsChecked(val === 'COMPLETED');
        },
      },
    );
  };

  const taskPriority: 'HIGH' | 'MEDIUM' | 'LOW' | 'Done' = useMemo(() => {
    switch (task.status) {
      case 'COMPLETED':
        return 'Done';
      case 'ARCHIVED':
        return 'Done';

      default:
        return task.priority as 'HIGH' | 'MEDIUM' | 'LOW' | 'Done';
    }
  }, [task.priority, task.status]);

  const isOverdue = useMemo(
    () => dayjs(task.dueAt).diff(dayjs(new Date()), 'd') <= -1 && !isChecked,
    [task.dueAt, isChecked],
  );

  return (
    <div
      key={task._id}
      className={cn(
        'text-secondary-dark',
        'text-xs',
        isChecked
          ? 'border-[#DEEEF8] bg-separator-primary'
          : 'bg-fill-tertiary',
        'rounded-[6px] py-4 px-[22px]',
        'border-2',
        'flex items-center gap-x-2',
        'hover:border-2 hover:border-secondary-dark',
        selectedTaskId === task._id
          ? 'border-2 border-secondary-dark'
          : 'border-transparent',
      )}
    >
      {!subscription?.isExpired && canEdit && (
        <button
          type="button"
          onClick={handleClickCheckbox}
          disabled={isLoading || updateStatusLoading}
        >
          <HiOutlineCheckCircle
            className={cn(
              'h-7 w-7',
              isChecked ? 'text-success-base' : 'text-[#D0DEEA]',
            )}
          />
        </button>
      )}
      <div>
        <p className="font-semibold">{task.type}</p>
        <div className="flex items-center justify-between">
          <p
            className={cn(
              'w-[300px] truncate text-lg font-semibold',
              isChecked && 'line-through',
            )}
          >
            {task.title}
          </p>
          <Status
            label={capitalize(taskPriority)}
            className={cn(
              'bg-transparent p-0 px-2 py-[3px]',
              'w-fit text-xs leading-5',
              getPriorityStyle(taskPriority),
            )}
          />
        </div>
        <div className="flex items-center gap-x-2">
          <p
            className={cn(
              task.status === 'IN_PROGRESS' && 'text-info-base',
              'font-semibold',
            )}
          >
            {task.status === 'IN_PROGRESS'
              ? 'In Progress'
              : capitalize(task.status)}
          </p>
          <p
            className={cn(
              isOverdue ? 'text-danger-base' : 'text-content',
              'flex gap-x-1',
            )}
          >
            Due Date: {dayjs(task.dueAt).format(c.DATE_FORMAT_V2)}
            {isOverdue && <span className="text-[9px]">(Overdue)</span>}
          </p>
        </div>
      </div>
      <div className="flex flex-1 items-center justify-end gap-x-20">
        <div className="flex gap-x-3.5">
          <Avatar size={SIZE.Medium} user={task.lead} />
          <div>
            <p className="truncate">Associated Contact</p>
            <p className="truncate text-sm font-semibold">
              {task.lead?.fullName}
            </p>
          </div>
        </div>
        <div>
          <p className="mb-1">Assigned to</p>
          <AvatarGroup users={task.accountUsers} size={SIZE.Small} />
        </div>
        <Dropdown
          customIcon={<GoChevronDown className="w-5 h-5 text-fill-tertiary" />}
          placeholder={
            STATUS_OPTIONS?.find(
              (status) => startCase(status.value) === startCase(task.status),
            )?.label || 'Dropdown'
          }
          className="w-[136px]"
          options={
            STATUS_OPTIONS?.map((status) => ({
              label: status.label,
              value: status.value,
              isSelected: startCase(status.value) === startCase(task.status),
            })) || []
          }
          onChange={(opt) => handleChangeStatus(opt.value as string)}
          selectedClassName={cn(
            'font-semibold text-fill-tertiary',
            isChecked
              ? 'bg-primary-base'
              : task.status === 'IN_PROGRESS'
                ? 'bg-info-base'
                : 'bg-secondary-dark',
          )}
          disabled={subscription?.isExpired || !canEdit}
        />
        <NavAction
          label={
            <TbDotsVertical
              className={cn(
                'mr-0 h-6 w-6 text-secondary-dark hover:text-primary-base',
              )}
            />
          }
          options={options}
          optionsClassName="w-[190px]"
        />
      </div>
    </div>
  );
};

export default TaskTile;
